import React, { memo } from "react";
import PropTypes from "prop-types";
import { BeatLoader } from "react-spinners";

/**
 * StatisticsCard Component
 * Displays a statistic with name, value, and optional trend graph
 * Memoized to prevent unnecessary re-renders
 * 
 * @component
 * @example
 * <StatisticsCard 
 *   name="Secondary Sales" 
 *   value="₹1.5M" 
 *   trendGraph={<LineGraph data={data} />}
 *   isLoading={false}
 * />
 */
const StatisticsCard = memo(({ name, value, trendGraph, rsi, percentage, isLoading = false }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm mx-2 p-2 transition-shadow mt-1">
      <div className="flex items-center justify-between">
        {/* Name Section */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-gray-700 truncate">
            {name}
          </h3>
        </div>

        {/* Trend Graph Section */}
        {trendGraph && (
          <div className="flex-shrink-0 ml-3 w-20 h-8">
            {trendGraph}
          </div>
        )}

        {/* Value Section */}
        <div className="flex-shrink-0  text-right">
          {isLoading ? (
            <BeatLoader color="#3901FF" size={8} />
          ) : (
            <>
              <div className="text-xl font-semibold text-[#3901FF]">
                {value}
              </div>
              {(rsi || percentage) && (
                <div className="text-xs text-[#3901FF] mt-1">
                  {rsi && <span className="mr-2">{rsi}</span>}
                  {percentage && <span>{percentage}</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

StatisticsCard.propTypes = {
  /** Name/label of the statistic */
  name: PropTypes.string.isRequired,
  /** Value to display */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Optional trend graph component */
  trendGraph: PropTypes.node,
  /** Optional RSI label */
  rsi: PropTypes.string,
  /** Optional percentage label */
  percentage: PropTypes.string,
  /** Loading state */
  isLoading: PropTypes.bool,
};

export default StatisticsCard;
